import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  COUNTRY_LANG_PAGE_SIZE,
  CountryLangFormModal,
  CountryLangSearchBar,
  CountryLangTable,
  DeleteCountryLangModal,
  useCountryLangFilters,
  useCountryLangList,
  useDeleteCountryLang,
} from '@/features/location';
import type { CountryLangViewModel } from '@/shared/api/generated';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/location/countries/$countryId/langs/')({
  component: CountryLangListPage,
});

/** Página de listagem de Idiomas de um país (área de Localização). */
export function CountryLangListPage() {
  const { t } = useTranslation('location');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { countryId: countryIdParam } = Route.useParams();
  const countryId = Number(countryIdParam);

  const { langs, countryName, isLoading, isError, refetch } = useCountryLangList(countryId);
  const { filters, updateFilters } = useCountryLangFilters();
  const deleteLang = useDeleteCountryLang(countryId);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CountryLangViewModel | null>(null);
  const [deleting, setDeleting] = useState<CountryLangViewModel | null>(null);

  // TODO: busca client-side — o endpoint de idiomas ainda não aceita parâmetro de
  // busca. Quando o backend implementar, enviar `filters.search` via
  // `useCountryLangList` e remover a filtragem abaixo.
  const searched = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    if (!query) return langs;
    return langs.filter(
      lang =>
        (lang.cultureDisplayName ?? '').toLowerCase().includes(query) ||
        (lang.cultureInfo ?? '').toLowerCase().includes(query) ||
        (lang.name ?? '').toLowerCase().includes(query),
    );
  }, [langs, filters.search]);

  // TODO: paginação client-side — o endpoint retorna a lista completa. Quando o
  // backend paginar, passar `page`/`pageSize` a `useCountryLangList`, usar os
  // totais do envelope e remover o fatiamento. Ver `COUNTRY_LANG_PAGE_SIZE`.
  const totalPages = Math.max(1, Math.ceil(searched.length / COUNTRY_LANG_PAGE_SIZE));
  const currentPage = Math.min(filters.page, totalPages);
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * COUNTRY_LANG_PAGE_SIZE;
    return searched.slice(start, start + COUNTRY_LANG_PAGE_SIZE);
  }, [searched, currentPage]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (lang: CountryLangViewModel) => {
    setEditing(lang);
    setFormOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleting?.id == null) return;
    deleteLang.mutate(
      { countryId, id: deleting.id },
      {
        onSuccess: () =>
          alert({ alertType: 'success', title: t('countryLangs.toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('countryLangs.toast.deleteError') }),
        onSettled: () => setDeleting(null),
      },
    );
  };

  const subtitle = countryName
    ? t('countryLangs.subtitle', { country: countryName, count: langs.length })
    : t('countryLangs.subtitleNoCountry', { count: langs.length });

  const addButton = (
    <Button hierarchy="primary" size="lg" leadingIcon={PlusIcon} onClick={openCreate}>
      {t('countryLangs.actions.add')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-xxs">
      <PageHeader
        title={t('countryLangs.title')}
        subtitle={subtitle}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to="/location/countries">{t('countryLangs.breadcrumb.countries')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('countryLangs.breadcrumb.langs')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={
          <div className="flex items-center gap-nano">
            <Button
              hierarchy="secondary"
              size="lg"
              leadingIcon={ArrowLeftIcon}
              onClick={() => void navigate({ to: '/location/countries' })}
            >
              {t('countryLangs.back')}
            </Button>
            {addButton}
          </div>
        }
      />

      {isError ? (
        <div className="flex flex-col items-start gap-nano rounded-md border-hairline border-base-danger-300 bg-ctx-layout-surface p-xxs">
          <p className="text-base-danger-700 ts-paragraph-xs">
            {t('countryLangs.toast.loadError')}
          </p>
          <Button hierarchy="secondary" size="md" onClick={() => void refetch()}>
            {t('countryLangs.loadErrorRetry')}
          </Button>
        </div>
      ) : (
        <>
          <CountryLangSearchBar
            defaultValue={filters.search}
            onSearch={search => updateFilters({ search, page: 1 })}
            onClear={() => updateFilters({ search: '', page: 1 })}
          />

          <CountryLangTable
            data={pageData}
            isLoading={isLoading}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={searched.length}
            pageSize={COUNTRY_LANG_PAGE_SIZE}
            onPageChange={page => updateFilters({ page })}
            emptyStateAction={addButton}
            onEdit={openEdit}
            onDelete={setDeleting}
          />
        </>
      )}

      <CountryLangFormModal
        open={formOpen}
        countryId={countryId}
        lang={editing}
        onOpenChange={open => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
      />

      <DeleteCountryLangModal
        open={deleting !== null}
        langName={deleting?.cultureDisplayName ?? deleting?.name ?? ''}
        onConfirm={handleDeleteConfirm}
        onOpenChange={open => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}
